import { useState } from "react";
import { Search, X } from "lucide-react";
import { useBalanceEditor, STAT_COLUMNS } from "@/lib/use-balance-editor";
import { LEVEL_FIELDS } from "@/lib/balance-schema";
import { cn } from "@/lib/utils";
import { WorkspaceShell, usePreviewReadOnly } from "@/components/workspace/WorkspaceShell";
import "./game-config.css";

const LEVEL_LABELS = ["Hero EXP", "Enemy EXP", "Enemy HP", "Enemy damage", "Enemy gold", "Enemy armor"];
const STAT_LABELS = STAT_COLUMNS.map(([, label]) => label);
const NUMBER_FORMAT = new Intl.NumberFormat("en-US", { maximumFractionDigits: 6 });
// Stats stored as whole numbers; every other stat is a percentage in tenths (2000 = 200%).
const FLAT_STATS = new Set(["Max HP", "Max MP", "Att. Damage", "Defense"]);
type CellFormat = "number" | "flat-stat" | "percent-stat";
const formatCell = (value: number, format: CellFormat) => {
  if (format !== "number" && value === -1) return "Uncapped";
  return format === "percent-stat" ? `${NUMBER_FORMAT.format(value / 10)}%` : NUMBER_FORMAT.format(value);
};

export function BalanceWorkspace() {
  const readOnly = usePreviewReadOnly();
  const editor = useBalanceEditor();
  const [tab, setTab] = useState<"levels" | "stats">("levels");
  const [filter, setFilter] = useState("");
  const [focused, setFocused] = useState<string | null>(null);
  const data = editor.base?.data;
  const labels = tab === "levels" ? LEVEL_LABELS : STAT_LABELS;
  const cell = (field: string, index: number, value: number, label: string, column: string, format: CellFormat = "number") => {
    const id = `${field}:${index}`;
    const text = editor.cellValue(id, value);
    const invalid = text.trim() === "" || !Number.isFinite(Number(text));
    // Show formatted values at rest; the raw stored value while editing.
    const shown = focused === id || invalid ? text : formatCell(Number(text), format);
    const muted = shown === "Uncapped";
    return <label key={field} className="gc-cell"><span>{column}</span>
      <input className={cn("ws-input", muted && "is-muted")} aria-label={label} aria-invalid={invalid} inputMode="decimal" value={shown} disabled={editor.save.isPending}
        onFocus={() => setFocused(id)} onBlur={() => setFocused(current => current === id ? null : current)} onChange={event => editor.edit(id, event.target.value)} /></label>;
  };
  const switchTab = (next: "levels" | "stats") => { setTab(next); setFilter(""); };

  return <WorkspaceShell crumbs={[{ label: "Game configuration" }, { label: "Balance" }]} readOnly={readOnly}>
    <div className="ws-scroll"><div className="ws-page">
      <div className="ws-head"><h1>Balance</h1>
        {data && <div className="ws-head-actions">
          {editor.dirty && <span className="pw-badge gc-badge-warn">Unsaved</span>}
          {editor.save.isSuccess && !editor.dirty && <span role="status" className="gc-status">Saved</span>}
          {editor.dirty && <button className="pw-button pw-button-primary" disabled={editor.issues.length > 0 || editor.save.isPending || readOnly} onClick={() => editor.save.mutate()}>
            {editor.save.isPending ? "Saving…" : "Save"}</button>}
        </div>}
      </div>
      {editor.query.isLoading && <div className="pw-empty" role="status"><span className="pw-loading-dot" />Loading balance…</div>}
      {editor.query.isError && <div className="pw-error" role="alert"><strong>Couldn’t load balance data</strong><p>{String(editor.query.error)}</p><button className="pw-button" onClick={() => void editor.query.refetch()}>Try again</button></div>}
      {data && <>
        {editor.save.error && <p className="ws-alert" role="alert">Couldn’t save: {String(editor.save.error)}</p>}
        {editor.issues.length > 0 && <div className="ws-alert" role="alert">Fix before saving:<ul className="gc-issues">{editor.issues.map(issue => <li key={issue}>{issue}</li>)}</ul></div>}
        <div className="gc-toolbar">
          <div className="ws-segmented" role="tablist" aria-label="Balance tables">
            <button role="tab" aria-selected={tab === "levels"} className={cn(tab === "levels" && "is-active")} onClick={() => switchTab("levels")}>Levels</button>
            <button role="tab" aria-selected={tab === "stats"} className={cn(tab === "stats" && "is-active")} onClick={() => switchTab("stats")}>Status caps</button>
          </div>
          <span className="gc-hint">{tab === "levels" && `Max level ${data.expTable.length - 1} · last row is the EXP boundary · `}{tab === "levels" ? "saved" : "Saved"} {new Date(editor.base!.updatedAt).toLocaleString()}</span>
          <div className="pw-toolbar-search"><Search size={14} aria-hidden="true" />
            <input aria-label={tab === "levels" ? "Find level" : "Find stat"} placeholder={tab === "levels" ? "Find level" : "Find stat"} value={filter} onChange={event => setFilter(event.target.value)} />
            {filter && <button type="button" className="pw-toolbar-clear" aria-label="Clear filter" onClick={() => setFilter("")}><X size={14} /></button>}</div>
        </div>
        <section className="ws-card is-flush gc-bal ws-table" role="tabpanel" aria-label={tab === "levels" ? "Level table" : "Status caps"} style={{ "--gc-cols": labels.length } as React.CSSProperties}>
          <div className="ws-table-row ws-table-head gc-bal-row"><span>{tab === "levels" ? "Level" : "Stat"}</span>{labels.map(label => <span key={label}>{label}</span>)}</div>
          {tab === "levels" ? data.expTable.map((_, index) => !String(index + 1).includes(filter.trim()) ? null : <div key={index} className="ws-table-row gc-bal-row">
            <span className="gc-bal-name">{index + 1}{index === data.expTable.length - 1 && <small>Terminal row</small>}</span>
            {LEVEL_FIELDS.map((field, column) => cell(field, index, data[field][index], `Level ${index + 1} ${LEVEL_LABELS[column]}`, LEVEL_LABELS[column]))}
          </div>) : data.statData.map((row, index) => !row.statName.toLowerCase().includes(filter.trim().toLowerCase()) ? null : <div key={index} className="ws-table-row gc-bal-row">
            <span className="gc-bal-name">{row.statName}</span>
            {STAT_COLUMNS.map(([field, label]) => cell(field, index, row[field], `${row.statName} ${label}`, label, FLAT_STATS.has(row.statName) ? "flat-stat" : "percent-stat"))}
          </div>)}
        </section>
      </>}
    </div></div>
  </WorkspaceShell>;
}
